import React, { useEffect, useMemo, useRef } from 'react';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import OrganisationDropdown from '../../../../../components/Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../../../../components/Shared/BotNumberDropdown/index.tsx';
import TemplateDropdown from '../../../../../components/Shared/TemplateDropdown/TemplateDropdown.tsx';
import ProfileDropdown from '../../../../../components/Shared/ProfileDropdown/index.tsx';
import RoleDropdown from '../../../../../components/Shared/RoleDropdown/index.tsx';
import SearchableDropdown from '../../../../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions, LeadFormActions } from '../../../../../Services/_actions/index.js';
import { userInformationAtom } from '../../../../../Services/_states/auth.js';
import { assignedCampaignUserAtom } from '../../../../../Services/_states/index.js';
import {
  getLoggedInOrganisation,
  isSuperAdmin,
} from '../../../../user-management/utils/userAccessUtils';

function getUserProfileId(user) {
  if (!user) return '';
  const raw = user.profile_id ?? user.profile?.id ?? user.profile;
  if (raw && typeof raw === 'object') {
    return String(raw.id ?? '').trim();
  }
  return String(raw ?? '').trim();
}

function userHasProfile(user, profileId) {
  if (!user || profileId == null) return false;
  return getUserProfileId(user) === String(profileId).trim();
}

function orgUsersCacheKey(organisationId) {
  return organisationId ? `org-users-${organisationId}` : '';
}

function mergeUsersById(primary = [], fallback = []) {
  const merged = [...primary];
  const seen = new Set(primary.map((user) => String(user?.id)));

  fallback.forEach((user) => {
    if (user?.id == null || seen.has(String(user.id))) return;
    merged.push(user);
    seen.add(String(user.id));
  });

  return merged;
}

function getStakeholderTarget(trigger) {
  if (trigger?.stakeholderTarget) return trigger.stakeholderTarget;
  if (trigger?.stakeholders === 'end_customer') return 'endcustomer';
  return 'profile';
}

function buildStakeholdersFromSelection(selectedProfiles, profileUserMap) {
  return (selectedProfiles || []).map((profile) => ({
    id: profile.id,
    name: profile.name,
    users: profileUserMap[String(profile.id)] || [],
  }));
}

export default function TriggerTypeFields({
  trigger,
  onChange,
  errors = {},
  workspaceId,
}) {
  const { fetchCampaignMappings } = LeadFormActions();
  const fetchCampaignMappingsRef = useRef(fetchCampaignMappings);
  fetchCampaignMappingsRef.current = fetchCampaignMappings;
  const constantActions = ConstantActions();
  const userInfo = useRecoilValue(userInformationAtom);
  const [userList, setUserList] = useRecoilState(assignedCampaignUserAtom);
  const loggedInOrg = getLoggedInOrganisation(userInfo);
  const isOrgUser = Boolean(loggedInOrg) && !isSuperAdmin(userInfo);
  const organisationId =
    trigger?.organisation?.organisation_id || loggedInOrg?.organisation_id || '';
  const orgUsersKey = orgUsersCacheKey(organisationId);
  const allUsers = (userList?.[orgUsersKey] ?? userList?.[workspaceId] ?? []) || [];
  const needsProfileUsers = ['assignLead', 'whatsapp'].includes(trigger?.trigger_type);
  const showOrganisationDropdown =
    ['whatsapp', 'bot'].includes(trigger?.trigger_type) && !isOrgUser;

  const selectedProfiles = trigger?.selectedProfiles || [];
  const profileUserMap = trigger?.profileUserMap || {};
  const stakeholderTarget = getStakeholderTarget(trigger);

  const [childMappings, setChildMappings] = React.useState([]);
  const loadedOrgUsersRef = useRef(new Set());
  const orgAppliedRef = useRef(false);

  useEffect(() => {
    orgAppliedRef.current = false;
    loadedOrgUsersRef.current = new Set();
  }, [trigger?.trigger_type]);

  useEffect(() => {
    if (trigger?.trigger_type !== 'moveToCampaign' || !workspaceId) return;
    fetchCampaignMappingsRef.current(workspaceId)
      .then((res) => {
        if (res?.status === 200) {
          setChildMappings(res.data?.child_mappings || []);
        }
      })
      .catch(() => setChildMappings([]));
  }, [workspaceId, trigger?.trigger_type]);

  useEffect(() => {
    if (!isOrgUser || !loggedInOrg || !needsProfileUsers) return;
    if (trigger?.organisation?.organisation_id) return;
    if (orgAppliedRef.current) return;
    orgAppliedRef.current = true;
    onChange?.({ ...trigger, organisation: loggedInOrg });
  }, [
    isOrgUser,
    loggedInOrg,
    needsProfileUsers,
    trigger?.organisation?.organisation_id,
    trigger?.trigger_type,
  ]);

  useEffect(() => {
    if (!organisationId || !needsProfileUsers || !orgUsersKey) return;
    if (loadedOrgUsersRef.current.has(orgUsersKey)) return;
    loadedOrgUsersRef.current.add(orgUsersKey);

    constantActions
      .getUserMinimalList(organisationId, {})
      .then((res) => {
        const users = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setUserList((prev) => ({ ...prev, [orgUsersKey]: users }));
      })
      .catch(() => {
        setUserList((prev) => ({ ...prev, [orgUsersKey]: [] }));
      });
  }, [constantActions, needsProfileUsers, orgUsersKey, organisationId, setUserList]);

  const updateTrigger = (patch) => {
    onChange?.({ ...trigger, ...patch });
  };

  const profileFields = useMemo(
    () =>
      (selectedProfiles || []).map((profile) => {
        const pid = String(profile?.id);
        const savedUsers = profileUserMap[pid] ?? [];
        const profileUsers = mergeUsersById(
          allUsers.filter((user) => userHasProfile(user, pid)),
          savedUsers
        );
        const globallySelectedIds = new Set(
          Object.values(profileUserMap)
            .flat()
            .map((user) => String(user.id))
        );

        return (
          <SearchableDropdown
            key={pid}
            options={profileUsers}
            value={savedUsers}
            labelKey="username"
            valueKey="id"
            label={`Select users — ${profile?.name ?? 'Profile'}`}
            multiSelect
            getOptionDisabled={(option) =>
              globallySelectedIds.has(String(option?.id))
              && !(savedUsers ?? []).some((u) => String(u.id) === String(option?.id))
            }
            onChange={(val) => {
              const nextUsers =
                (val ?? []).map((user) => ({
                  id: user?.id,
                  username: user?.username ?? user?.name,
                })) || [];
              const nextMap = {
                ...profileUserMap,
                [pid]: nextUsers,
              };
              updateTrigger({
                profileUserMap: nextMap,
                selectedProfiles,
                stakeholders: buildStakeholdersFromSelection(selectedProfiles, nextMap),
              });
            }}
          />
        );
      }),
    [allUsers, profileUserMap, selectedProfiles, trigger]
  );

  if (!trigger) return null;

  const handleProfilesChange = (val) => {
    const profiles = val?.map((p) => ({ id: p?.id, name: p?.name ?? p?.label })) ?? [];
    const nextMap = Object.fromEntries(
      Object.entries(profileUserMap).filter(([key]) =>
        profiles.some((profile) => String(profile.id) === String(key))
      )
    );
    updateTrigger({
      selectedProfiles: profiles,
      profileUserMap: nextMap,
      stakeholders: buildStakeholdersFromSelection(profiles, nextMap),
    });
  };

  return (
    <Stack spacing={2}>
      {trigger.trigger_type === 'email' ? (
        <TextField
          label="Sender email"
          size="small"
          fullWidth
          required
          value={trigger.sender_email || ''}
          onChange={(event) => updateTrigger({ sender_email: event.target.value })}
          error={Boolean(errors.sender_email)}
          helperText={errors.sender_email}
        />
      ) : null}

      {trigger.trigger_type === 'sms' ? (
        <>
          <TextField
            label="Sender ID"
            size="small"
            fullWidth
            required
            value={trigger.sender_id || ''}
            onChange={(event) => updateTrigger({ sender_id: event.target.value })}
            error={Boolean(errors.sender_id)}
            helperText={errors.sender_id}
          />
          <TextField
            label="DLT ID"
            size="small"
            fullWidth
            value={trigger.dlt_id || ''}
            onChange={(event) => updateTrigger({ dlt_id: event.target.value })}
          />
          <RoleDropdown
            value={trigger.stakeholders ?? []}
            onChange={(val) => {
              const selected =
                val?.map((user) => ({ id: user?.id, name: user?.name })) ?? [];
              updateTrigger({ stakeholders: selected });
            }}
            organisationId={organisationId}
            multiSelect
            label="Select stakeholders"
          />
        </>
      ) : null}

      {showOrganisationDropdown ? (
        <OrganisationDropdown
          label="Organisation"
          value={trigger.organisation ?? null}
          onChange={(org) => {
            const selected = org?.organisation_id
              ? { name: org.name, organisation_id: org.organisation_id }
              : null;
            updateTrigger({
              organisation: selected,
              template: null,
              template_id: '',
              botNumber: null,
              bot_number: '',
              profileUserMap: {},
              selectedProfiles: [],
              users: [],
              stakeholders: [],
            });
          }}
          autoSelectValue={false}
        />
      ) : null}

      {['whatsapp', 'bot'].includes(trigger.trigger_type) ? (
        <BotNumberDropdown
          organisation={
            trigger.organisation
              ?? (loggedInOrg ? { organisation_id: loggedInOrg.organisation_id, name: loggedInOrg.name } : null)
          }
          label="Bot number"
          value={trigger.botNumber || null}
          onChange={(val) => {
            if (val?.wa_phone_number) {
              updateTrigger({
                bot_number: val.wa_phone_number,
                botNumber: {
                  wa_phone_number: val.wa_phone_number,
                  app_name: val.app_name,
                },
                template: null,
                template_id: '',
              });
            } else {
              updateTrigger({ bot_number: '', botNumber: null, template: null, template_id: '' });
            }
          }}
        />
      ) : null}

      {trigger.trigger_type === 'whatsapp' ? (
        <>
          <TemplateDropdown
            value={trigger.template || null}
            dropdownFilters={{
              organisationId: trigger.organisation?.organisation_id || '',
              botNumber: trigger.bot_number || '',
            }}
            onChange={(val) => {
              if (val?.wa_Id) {
                updateTrigger({
                  template_id: val?.md_id ?? val?.wa_Id,
                  template: { wa_Id: val.wa_Id, md_id: val.md_id, name: val.name },
                });
              } else {
                updateTrigger({ template_id: '', template: null });
              }
            }}
            label="Template"
            disabled={!trigger.organisation?.organisation_id || !trigger.bot_number}
          />
          <TextField
            select
            label="Send to"
            size="small"
            fullWidth
            value={stakeholderTarget}
            onChange={(event) => {
              const val = event.target.value;
              if (val === 'endcustomer') {
                updateTrigger({
                  stakeholderTarget: 'endcustomer',
                  stakeholders: 'end_customer',
                  users: [],
                  selectedProfiles: [],
                  profileUserMap: {},
                });
              } else {
                updateTrigger({ stakeholderTarget: 'profile', stakeholders: [] });
              }
            }}
          >
            <MenuItem value="endcustomer">End customer</MenuItem>
            <MenuItem value="profile">Profile</MenuItem>
          </TextField>
          {stakeholderTarget === 'profile' ? (
            <>
              <ProfileDropdown
                value={selectedProfiles}
                onChange={handleProfilesChange}
                organisationId={organisationId}
                multiSelect
                label="Profiles"
              />
              {profileFields}
            </>
          ) : null}
        </>
      ) : null}

      {trigger.trigger_type === 'bot' ? (
        <TextField
          label="Flow ID"
          size="small"
          fullWidth
          required
          value={trigger.flow_id || ''}
          onChange={(event) => updateTrigger({ flow_id: event.target.value })}
          error={Boolean(errors.flow_id)}
          helperText={errors.flow_id}
        />
      ) : null}

      {trigger.trigger_type === 'moveToCampaign' ? (
        <SearchableDropdown
          options={childMappings}
          labelKey="child_campaign_name"
          valueKey="child_campaign_id"
          label="Child campaign"
          value={trigger.childCampaign}
          onChange={(val) => updateTrigger({ childCampaign: val || null })}
        />
      ) : null}

      {trigger.trigger_type === 'assignLead' ? (
        <>
          <ProfileDropdown
            value={selectedProfiles}
            onChange={handleProfilesChange}
            organisationId={organisationId}
            multiSelect
            label="Profiles"
          />
          {profileFields}
          {errors.users ? (
            <Typography variant="caption" color="error">
              {errors.users}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
